/**
 * DiversityInSchemes.jsx  —  view_id 10
 *
 * Self-contained: fetches its own data, manages its own state.
 * Receives only: pid (string)
 */

import React, { useState, useEffect, useRef } from 'react';
import Select    from 'react-select';
import { CSVLink } from 'react-csv';

const API = 'https://api-project360-insights.haqdarshak.com';

const DiversityInSchemes = ({ pid }) => {
  const [apiData, setApiData] = useState({
    scheme_diversity: {},
    schemes: [],
    states: [],
    total_cases: 0,
  });
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [statesPerPage,  setStatesPerPage]  = useState(3);
  const [schemesPerPage, setSchemesPerPage] = useState(5);
  const [selectedStates,  setSelectedStates]  = useState([]);
  const [selectedSchemes, setSelectedSchemes] = useState([]);
  const [projectStates,   setProjectStates]   = useState([]);
  const [projectSchemes,  setProjectSchemes]  = useState([]);
  const [statePage,  setStatePage]  = useState(0);
  const [schemePage, setSchemePage] = useState(0);
  const tableRef = useRef(null);

  // State for info popups
  const [infoPopup, setInfoPopup] = useState({ show: false, title: '', description: '', example: '' });

  // Definitions for info popups
  const definitions = {
    "Scheme Wise Application Statistics": {
      title: "Scheme Wise Application Statistics",
      description: "This dashboard provides comprehensive statistics about applications across different schemes. It shows the distribution of applications by state, benefit values, and application statuses.",
      example: "• Scheme Name: Name of the government scheme\n• Applications (State-wise): Number of applications per state\n• Total Applications: Total applications across all states\n• Applications with Benefit Received: Applications that successfully received benefits\n• Total Benefit Value: Total monetary value of benefits disbursed"
    },
    "States": {
      title: "States",
      description: "Filter by state to view scheme-wise application statistics for specific geographical regions.",
      example: "Example: Maharashtra, Karnataka, Delhi, Tamil Nadu, etc."
    },
    "Schemes": {
      title: "Schemes",
      description: "Filter by specific schemes to view detailed application statistics for selected government schemes.",
      example: "Example: PM Awas Yojana, Ayushman Bharat, PM Kisan Samman Nidhi, etc."
    },
    "Scheme Name": {
      title: "Scheme Name",
      description: "The name of the government scheme for which application statistics are displayed.",
      example: "Example: Pradhan Mantri Awas Yojana, National Health Mission, etc."
    },
    "Benefit Value": {
      title: "Benefit Value",
      description: "The monetary value of the benefit provided under each scheme. This represents the financial assistance or benefit amount.",
      example: "Example: ₹2,50,000 for housing schemes, ₹5,00,000 for health insurance, etc."
    },
    "% of Total": {
      title: "% of Total",
      description: "The percentage of total applications that this scheme or state represents. Calculated as (scheme applications / total applications) × 100.",
      example: "If a scheme has 100 applications out of 1000 total, it represents 10% of total."
    },
    "Total Applications": {
      title: "Total Applications",
      description: "The total number of applications across all statuses for each scheme or state.",
      example: "Sum of applications opened, submitted, benefit received, etc."
    },
    "Applications with Benefit Received": {
      title: "Applications with Benefit Received",
      description: "The count of applications that have successfully received benefits. These are applications where proof of benefit has been uploaded.",
      example: "Applications where citizens have confirmed receiving the benefit."
    },
    "Total Benefit Value": {
      title: "Total Benefit Value",
      description: "The total monetary value of benefits disbursed across all applications for each scheme. This represents the total financial impact.",
      example: "If 100 applications each received ₹10,000, the Total Benefit Value = ₹10,00,000"
    }
  };

  // Info Button Component
  const InfoButton = ({ title }) => {
    const definition = definitions[title];
    if (!definition) return null;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setInfoPopup({
            show: true,
            title: definition.title,
            description: definition.description,
            example: definition.example || ''
          });
        }}
        className="group relative ml-1.5 inline-flex items-center justify-center w-4 h-4 flex-shrink-0 rounded-full text-gray-400 transition-colors duration-300 ease-out hover:text-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-400"
        aria-label={`Info about ${title}`}
      >
        <span className="absolute inset-0 -m-2 rounded-full bg-white/0 ring-1 ring-transparent transition-all duration-300 ease-out group-hover:bg-white/70 group-hover:backdrop-blur-md group-hover:ring-black/5 group-hover:shadow-[0_4px_14px_rgba(99,102,241,0.18)]" />

        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="relative w-3.5 h-3.5 transition-transform duration-300 ease-out group-hover:scale-110"
        >
          <circle cx="12" cy="12" r="9.5" />
          <path d="M12 16v-4.5" />
          <path d="M12 7.8h.01" />
        </svg>
      </button>
    );
  };

  // Info Popup Component
  const InfoPopup = () => {
    if (!infoPopup.show) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]" onClick={closeInfoPopup}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-0 overflow-hidden animate-scaleIn" onClick={(e) => e.stopPropagation()}>
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 18a9 9 0 100-18 9 9 0 000 18z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">{infoPopup.title}</h3>
            </div>
            <button
              onClick={closeInfoPopup}
              className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="mt-0.5">
                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-700 leading-relaxed">{infoPopup.description}</p>
            </div>

            {infoPopup.example && (
              <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📊</span>
                  <p className="text-sm font-semibold text-gray-800">Example</p>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line font-mono bg-white/50 p-3 rounded-lg border border-blue-100">
                  {infoPopup.example}
                </p>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeInfoPopup}
                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Close info popup
  const closeInfoPopup = () => {
    setInfoPopup({ show: false, title: '', description: '', example: '' });
  };

  // Filter options
  useEffect(() => {
    if (!pid) return;
    Promise.all([
      fetch(`${API}/kpi/scheme-diversity/unique-state/?pid=${encodeURIComponent(pid)}`),
      fetch(`${API}/kpi/scheme-diversity/unique-scheme/?pid=${encodeURIComponent(pid)}`),
    ])
      .then(([sr, scr]) => Promise.all([sr.json(), scr.json()]))
      .then(([sd, scd]) => {
        setProjectStates(sd.states.map(s => ({ value: s, label: s })));
        setProjectSchemes(scd.schemes.map(s => ({ value: s, label: s })));
      })
      .catch(err => console.error('[DiversityInSchemes] options error:', err));
  }, [pid]);

  const fetchData = async (states = selectedStates, schemes = selectedSchemes) => {
    if (!pid) return;
    setLoading(true);
    setError(null);
    try {
      const p = new URLSearchParams({ pid });
      states.forEach(s  => p.append('State',      s.value));
      schemes.forEach(s => p.append('SchemeName', s.value));
      const res  = await fetch(`${API}/kpi/scheme-diversity/?${p}`);
      if (!res.ok) throw new Error('Network error');
      const data = await res.json();
      setApiData(data);
      setStatePage(0);
      setSchemePage(0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (pid) fetchData(); }, [pid]);

  const applyFilters = () => fetchData();
  const clearFilters = () => {
    setSelectedStates([]);
    setSelectedSchemes([]);
    fetchData([], []);
  };

  // Format number with commas (Indian number system)
  const formatNumber = (num) => {
    if (num === null || num === undefined || num === '') return '—';
    if (typeof num === 'string' && num.includes('%')) return num;
    const number = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(number)) return num;
    return number.toLocaleString('en-IN');
  };

  // Format currency with ₹ symbol and Indian number system
  const formatCurrency = (num) => {
    if (num === null || num === undefined || num === '') return '—';
    const number = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(number)) return num;
    return `₹${number.toLocaleString('en-IN')}`;
  };

  const prepareCSV = () => {
    const { scheme_diversity, schemes, states } = apiData;
    const headers = ['Scheme Name', 'Benefit Value', ...states, '% of Total', 'Total Applications', 'Applications with Benefit Received', 'Total Benefit Value'];
    const rows = schemes.map(s => [
      s,
      scheme_diversity[s]?.SchemeValue ?? '',
      ...states.map(st => scheme_diversity[s]?.[st] || '0%'),
      scheme_diversity[s]?.['% of Total'] || '0%',
      scheme_diversity[s]?.total_cases || 0,
      scheme_diversity[s]?.br_count    || 0,
      scheme_diversity[s]?.total_scheme_value || 0,
    ]);
    const total = scheme_diversity.column_total ? [[
      'Total', '',
      ...states.map(st => scheme_diversity.column_total[st] || '0%'),
      scheme_diversity.column_total['% of Total'] || '100%',
      apiData.total_cases || 0,
      scheme_diversity.column_total.br_count || 0,
      scheme_diversity.column_total.total_scheme_value || 0,
    ]] : [];
    return [headers, ...rows, ...total];
  };

  const pStates  = apiData.states?.slice(statePage  * statesPerPage,  (statePage  + 1) * statesPerPage)  || [];
  const pSchemes = apiData.schemes?.slice(schemePage * schemesPerPage, (schemePage + 1) * schemesPerPage) || [];
  const totalSP  = Math.ceil((apiData.states?.length  || 0) / statesPerPage)  || 1;
  const totalScP = Math.ceil((apiData.schemes?.length || 0) / schemesPerPage) || 1;

  if (error) return <div className="p-4 text-red-500">Error: {error}</div>;

  return (
    <div className="border max-w-[92vw] border-gray-200 rounded-md ml-[3vw] p-4 my-4 shadow-md shadow-black">
      
      {/* Header with Info Button */}
      <div className="flex items-center px-4 py-2 w-full">
        <h3 className="text-xl font-bold text-pink-800">Scheme Wise Application Statistics</h3>
        <InfoButton title="Scheme Wise Application Statistics" />
      </div>

      {/* Subtitle */}
      <div className="px-4 pb-2">
        <p className="text-sm text-pink-600">Scheme-wise distribution of applications, benefits, and financial impact</p>
      </div>

      {/* Filters */}
      <div className="p-4 bg-white shadow rounded-lg mb-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <div className="flex items-center mb-1">
              <label className="text-sm font-medium text-gray-700">State</label>
              <InfoButton title="States" />
            </div>
            <Select isMulti options={projectStates} value={selectedStates} onChange={setSelectedStates} placeholder="All States" classNamePrefix="react-select" />
          </div>
          <div className="md:col-span-3">
            <div className="flex items-center mb-1">
              <label className="text-sm font-medium text-gray-700">Scheme Name</label>
              <InfoButton title="Schemes" />
            </div>
            <Select isMulti options={projectSchemes} value={selectedSchemes} onChange={setSelectedSchemes} placeholder="All Schemes" classNamePrefix="react-select" />
          </div>
          <div className="flex col-span-2 items-end space-x-2">
            <button onClick={applyFilters} disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50">
              {loading ? 'Loading…' : 'Apply'}
            </button>
            <button onClick={clearFilters} disabled={loading}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition disabled:opacity-50">
              Clear
            </button>
            <CSVLink data={prepareCSV()} filename="scheme-diversity.csv"
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition">
              Export CSV
            </CSVLink>
          </div>
        </div>
      </div>

      {/* Pagination controls */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg mb-3">
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">States per page:</label>
          <select value={statesPerPage} onChange={e => setStatesPerPage(Number(e.target.value))}
            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded-md">
            {[1,2,3,5,10,20].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">Schemes per page:</label>
          <select value={schemesPerPage} onChange={e => setSchemesPerPage(Number(e.target.value))}
            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded-md">
            {[5,10,20,50,100,200,500,1000].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {/* State pagination nav */}
      {apiData.states?.length > statesPerPage && (
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg mb-2">
          <span className="text-sm font-medium text-blue-800">
            States {statePage * statesPerPage + 1}–{Math.min((statePage+1)*statesPerPage, apiData.states.length)} of {apiData.states.length}
          </span>
          <div className="flex space-x-2">
            <button onClick={() => setStatePage(p => Math.max(0,p-1))} disabled={statePage===0}
              className="px-3 py-1 text-sm text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-50 disabled:opacity-50">Prev</button>
            <span className="px-3 py-1 text-sm">Page {statePage+1} / {totalSP}</span>
            <button onClick={() => setStatePage(p => Math.min(totalSP-1,p+1))} disabled={statePage>=totalSP-1}
              className="px-3 py-1 text-sm text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-50 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {/* Scheme pagination nav */}
      {apiData.schemes?.length > schemesPerPage && (
        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg mb-3">
          <span className="text-sm font-medium text-green-800">
            Schemes {schemePage * schemesPerPage + 1}–{Math.min((schemePage+1)*schemesPerPage, apiData.schemes.length)} of {apiData.schemes.length}
          </span>
          <div className="flex space-x-2">
            <button onClick={() => setSchemePage(p => Math.max(0,p-1))} disabled={schemePage===0}
              className="px-3 py-1 text-sm text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50 disabled:opacity-50">Prev</button>
            <span className="px-3 py-1 text-sm">Page {schemePage+1} / {totalScP}</span>
            <button onClick={() => setSchemePage(p => Math.min(totalScP-1,p+1))} disabled={schemePage>=totalScP-1}
              className="px-3 py-1 text-sm text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white max-w-[90vw] rounded-lg shadow-lg">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mx-auto" />
              <p className="mt-4 text-lg font-medium text-gray-700">Loading…</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto" ref={tableRef}>
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-3 text-center text-sm font-semibold uppercase border border-gray-300">
                    <div className="flex items-center justify-center">
                      <span>Scheme Name</span>
                      <InfoButton title="Scheme Name" />
                    </div>
                  </th>
                  <th className="py-2 px-3 text-center text-sm font-semibold uppercase border border-gray-300">
                    <div className="flex items-center justify-center">
                      <span>Benefit Value</span>
                      <InfoButton title="Benefit Value" />
                    </div>
                  </th>
                  {pStates.map(st => (
                    <th key={st} className="py-2 px-3 text-center text-sm font-semibold uppercase border border-gray-300">{st}</th>
                  ))}
                  <th className="py-2 px-3 text-center text-sm font-semibold uppercase border border-gray-300">
                    <div className="flex items-center justify-center">
                      <span>% of Total</span>
                      <InfoButton title="% of Total" />
                    </div>
                  </th>
                  <th className="py-2 px-3 text-center text-sm font-semibold uppercase border border-gray-300">
                    <div className="flex items-center justify-center">
                      <span>Total Applications</span>
                      <InfoButton title="Total Applications" />
                    </div>
                  </th>
                  <th className="py-2 px-3 text-center text-sm font-semibold uppercase border border-gray-300">
                    <div className="flex items-center justify-center">
                      <span>Applications with<br/>Benefit Received</span>
                      <InfoButton title="Applications with Benefit Received" />
                    </div>
                  </th>
                  <th className="py-2 px-3 text-center text-sm font-semibold uppercase border border-gray-300">
                    <div className="flex items-center justify-center">
                      <span>Total Benefit Value</span>
                      <InfoButton title="Total Benefit Value" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pSchemes.map(scheme => (
                  <tr key={scheme} className="hover:bg-blue-50 transition-colors">
                    <td className="py-2 px-3 text-left font-medium border border-gray-200 whitespace-nowrap text-gray-800">{scheme}</td>
                    <td className="py-2 px-3 text-right border border-gray-200 whitespace-nowrap text-gray-700">
                      {apiData.scheme_diversity[scheme]?.SchemeValue ? formatCurrency(apiData.scheme_diversity[scheme].SchemeValue) : '—'}
                    </td>
                    {pStates.map(st => (
                      <td key={`${scheme}-${st}`} className="py-2 px-3 text-right border border-gray-200 whitespace-nowrap text-gray-700">
                        {apiData.scheme_diversity[scheme]?.[st] || '0%'}
                      </td>
                    ))}
                    <td className="py-2 px-3 text-right border border-gray-200 whitespace-nowrap text-gray-700">{apiData.scheme_diversity[scheme]?.['% of Total']}</td>
                    <td className="py-2 px-3 text-right border border-gray-200 whitespace-nowrap text-gray-700">{formatNumber(apiData.scheme_diversity[scheme]?.total_cases || 0)}</td>
                    <td className="py-2 px-3 text-right border border-gray-200 whitespace-nowrap text-gray-700">{formatNumber(apiData.scheme_diversity[scheme]?.br_count)}</td>
                    <td className="py-2 px-3 text-right border border-gray-200 whitespace-nowrap font-semibold text-green-700">
                      {apiData.scheme_diversity[scheme]?.total_scheme_value ? formatCurrency(apiData.scheme_diversity[scheme].total_scheme_value) : '—'}
                    </td>
                  </tr>
                ))}

                {apiData.scheme_diversity?.column_total && (
                  <tr className="bg-blue-100 font-semibold">
                    <td className="py-2 px-3 text-left border border-gray-300 whitespace-nowrap">Total</td>
                    <td className="py-2 px-3 text-center border border-gray-300 whitespace-nowrap">—</td>
                    {pStates.map(st => (
                      <td key={`total-${st}`} className="py-2 px-3 text-right border border-gray-300 whitespace-nowrap">
                        {apiData.scheme_diversity.column_total[st] || '0%'}
                      </td>
                    ))}
                    <td className="py-2 px-3 text-right border border-gray-300 whitespace-nowrap">{apiData.scheme_diversity.column_total['% of Total']}</td>
                    <td className="py-2 px-3 text-right border border-gray-300 whitespace-nowrap">{formatNumber(apiData.total_cases || 0)}</td>
                    <td className="py-2 px-3 text-right border border-gray-300 whitespace-nowrap">{formatNumber(apiData.scheme_diversity.column_total.br_count)}</td>
                    <td className="py-2 px-3 text-right border border-gray-300 whitespace-nowrap text-green-700">
                      {apiData.scheme_diversity.column_total.total_scheme_value ? formatCurrency(apiData.scheme_diversity.column_total.total_scheme_value) : '—'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Popup */}
      <InfoPopup />

      <style jsx>{`
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
};

export default DiversityInSchemes;